import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Button,
  FormLabel,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";

const Refunds = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Top cards data
  const topCardsData = [
    {
      id: 1,
      value: "24",
      label: "Total Credits",
      icon: "solar:dollar-outline",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 2,
      value: "$12,500",
      label: "Credit Balance",
      icon: "streamline:bag-dollar",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
    },
    {
      id: 3,
      value: "18",
      label: "Applied",
      icon: "simple-line-icons:check",
      iconColor: "#525866",
      bgColor: "#7C8DB51F",
    },
    {
      id: 4,
      value: "6",
      label: "Pending",
      icon: "material-symbols:pending-outline",
       iconColor: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  // Refund records data
  const refundRecords = [
    {
      id: "CM-2024-001",
      customer: "ABC Construction Corp",
      originalInvoice: "NVV-2024-001",
      lastSync: "2 hours ago",
      date: "2/14/2024",
      creditDetails: {
        reason: "Partial work cancellation",
        status: "Applied as a Credit",
      },
      creditBalance: "$2,500.00",
      description: "Customer 'ABC Construction Corp' has a credit balance of $2,500.00 that can be applied to future invoices or refunded directly.",
    },
    {
      id: "CM-2024-002",
      customer: "ABC Construction Corp",
      originalInvoice: "NVV-2024-001",
      lastSync: "2 hours ago",
      date: "2/14/2024",
      creditDetails: {
        reason: "Material return credit",
        status: "Applied as a Credit",
      },
      creditBalance: "$2,500.00",
      description: "Customer 'ABC Construction Corp' has a credit balance of $2,500.00 that can be applied to future invoices or refunded directly.",
    },
    {
      id: "CM-2024-003",
      customer: "ABC Construction Corp",
      originalInvoice: "NVV-2024-001",
      lastSync: "Never",
      date: "2/14/2024",
      creditDetails: {
        reason: "Service adjustment",
        status: "Pending",
      },
      creditBalance: "$2,500.00",
      description: "Customer 'ABC Construction Corp' has a credit balance of $2,500.00 that can be applied to future invoices or refunded directly.",
    },
  ];

  // Status chip styling and color mapping
  const getStatusConfig = (status) => {
    const statusConfig = {
      "Applied as a Credit": {
        color: "#E8F5E9",
        textColor: "#23A26D",
        label: "Applied as Credit",
      },
      "Pending": {
        color: "#FFF3E0",
        textColor: "#DD992F",
        label: "Pending",
      },
    };

    return statusConfig[status] || {
      color: "#f0f0f1",
      textColor: "#666",
      label: status,
    };
  };

  const getStatusChip = (status) => {
    const config = getStatusConfig(status);

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
          minWidth: 80,
        }}
      />
    );
  };

  const handlePageBack = () => {
    navigate("/quickbooks");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Quickbooks/Refunds"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        {/* Single Main Card containing everything */}
        <Card sx={{  overflow: "hidden" }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header and Sync Now Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", mb: 4 }}>
              <Button
                variant="contained"
                color="main"
                size="medium"
                startIcon={
                  <Icon icon="heroicons:arrow-path" width={20} height={20} />
                }
              >
                Sync Now
              </Button>
            </Box>

            {/* Top Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {topCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 160,
                      }}
                    >
                      {/* Icon on the left */}
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              {/* Rounded box for search and filters */}
              <Card 
                sx={{ 
                  borderRadius: 2, 
                  border: "1px solid", 
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  p: 2
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" ,p:2}}>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        Credit Memos & Refunds 
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                    QuickBooks credit memos and negative invoices sync into Cleri as customer credits.
                    </Typography>
                  </Box>

                  {/* Search and Filters on the right */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <CustomTextField
                      placeholder="Search"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ 
                        width: 200,
                        '& .MuiOutlinedInput-root': {
                          height: '30px'
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon icon="tabler:search" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                {/* Refund Records List */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {refundRecords.map((refund, index) => (
                    <Card
                      key={refund.id}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        overflow: "visible",
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        {/* Refund Header */}
                        <Box sx={{ p: 3, pb: 2 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, p: 2 }}>
                            <Typography variant="h5">
                              {refund.id}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: "#DA1212" 
                                }}
                              >
                                {refund.creditBalance}
                              </Typography>
                              {getStatusChip(refund.creditDetails.status)}
                            </Box>
                          </Box>
                          
                          <Card
                            sx={{
                              p: 3,
                              borderRadius: 2,
                            //   border: "1px solid",
                            //   borderColor: "#003A700D",
                            //   backgroundColor: '#003A700D',
                              mb: 0,
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  Customer
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {refund.customer}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  Original Invoice
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {refund.originalInvoice}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  Last Sync
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {refund.lastSync}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  Date
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {refund.date}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Card>
                        </Box>


                        {/* Credit Details Section */}
                        <Box sx={{ px: 3 }}>
                          {/* Credit Details Box */}
                          <Card
                            sx={{
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "#DA12121A",
                              backgroundColor: '#DA12120D',
                              mb: 3,
                            }}
                          >
                            <CardContent sx={{ px: 8}}>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                  Credit Details
                                </Typography>
                                
                                {/* Reason Row */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="body1"sx={{ minWidth: 100 }}>
                                    Reason
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 530, flex: 1, textAlign: "right" }}>
                                    {refund.creditDetails.reason}
                                  </Typography>
                                </Box>

                                
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography variant="body1" sx={{ minWidth: 100 }}>
                                    Card Status
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 530, flex: 1, textAlign: "right" }}>
                                    {refund.creditDetails.status}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>

                          {/* Credit Memos & Refunds Section */}
                          <Card
                            sx={{
                              borderRadius: 2,
                            //   border: "1px solid",
                            //   borderColor: "#97A4B10D",
                            //   backgroundColor: '#97A4B10D',
                              mb: 0,
                            }}
                          >
                            <CardContent sx={{ px: 5 }}>
                              <Typography variant="h6" sx={{ mb: 2 }}>
                                Credit Memos & Refunds
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {refund.description}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Card>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </UserLayout>
  );
};

export default Refunds;